import { useEffect, useState } from "react";
import { Youtube } from "lucide-react";

// Row horizontal de YouTube Shorts (9:16). Usa lite-youtube-embed para no cargar iframe hasta el click.
// - Snap horizontal scroll para mobile.
// - Default render: thumbnail + play button; iframe se inyecta solo al primer click del usuario.
export default function YouTubeShortsRow({
    shorts = [],
    title = "Mas videos del canal",
    subtitle = "Shorts del canal Eclipse Cubiertas",
    testId = "youtube-shorts-row",
    bg = "bg-eclipse-light",
}) {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await import("lite-youtube-embed");
            await import("lite-youtube-embed/src/lite-yt-embed.css");
            if (!cancelled) setReady(true);
        })();
        return () => { cancelled = true; };
    }, []);

    if (!shorts || shorts.length === 0) return null;

    return (
        <section data-testid={testId} className={`${bg} py-14 sm:py-16`}>
            <div className="container-eclipse">
                <div className="flex items-end justify-between mb-7 gap-4 flex-wrap">
                    <div>
                        <p className="font-display font-bold text-[11px] tracking-[0.3em] uppercase text-eclipse-cta mb-2">
                            Shorts
                        </p>
                        <h3 className="font-display font-black text-eclipse-primary text-2xl sm:text-3xl tracking-tight leading-tight">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-eclipse-muted text-sm sm:text-base mt-1">{subtitle}</p>
                        )}
                    </div>
                    <a
                        href="https://www.youtube.com/@eclipsegrupo"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${testId}-channel-link`}
                        className="inline-flex items-center gap-2 text-xs font-display font-bold tracking-wider text-eclipse-primary hover:text-eclipse-cta transition-colors"
                    >
                        <Youtube className="w-4 h-4" />
                        Ver canal completo
                    </a>
                </div>

                <div
                    className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scroll-smooth"
                    data-testid={`${testId}-track`}
                >
                    {shorts.map((short) => (
                        <article
                            key={short.videoId}
                            data-testid={`${testId}-item-${short.videoId}`}
                            className="flex-shrink-0 w-[68%] sm:w-56 md:w-60 lg:w-64 snap-start"
                        >
                            <div className="relative aspect-[9/16] rounded-xl overflow-hidden shadow-xl bg-black">
                                {ready ? (
                                    <lite-youtube
                                        videoid={short.videoId}
                                        playlabel={short.title || "Short Eclipse Cubiertas"}
                                        style={{
                                            backgroundImage: `url(https://i.ytimg.com/vi/${short.videoId}/hqdefault.jpg)`,
                                            width: "100%",
                                            height: "100%",
                                            aspectRatio: "9 / 16",
                                            position: "absolute",
                                            inset: 0,
                                            maxWidth: "100%",
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="lty-playbtn"
                                            aria-label={`Reproducir: ${short.title || "Short"}`}
                                        />
                                    </lite-youtube>
                                ) : (
                                    <img
                                        src={`https://i.ytimg.com/vi/${short.videoId}/hqdefault.jpg`}
                                        alt={short.title || "Short Eclipse Cubiertas"}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            {short.title && (
                                <p className="mt-3 text-xs sm:text-sm font-medium text-eclipse-primary line-clamp-2 leading-snug">
                                    {short.title}
                                </p>
                            )}
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
